import { Component, Inject, OnInit } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Property } from '../../model/property';
import { PropertySearch } from '../../model/property_search';
import { Router } from '@angular/router';
import { PropertyService } from '../../core/services/property/property.service';
import { saveAs } from 'file-saver';
import { Subject } from 'rxjs';
import { ExportFooterComponent } from '../export-footer/export-footer.component';
import { StorageService } from '../../core/services/storage/storage-service.service';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
  selector: 'app-properties',
  standalone: true,
  templateUrl: './properties.component.html',
  styleUrls: ['./properties.component.css'],
  providers: [DatePipe],

  imports: [
    CommonModule,
    ReactiveFormsModule,
    ExportFooterComponent,
    MatTooltipModule
  ]
})
export class PropertiesComponent implements OnInit {

  public sheet: GC.Spread.Sheets.Worksheet = null;
  public spread: GC.Spread.Sheets.Workbook;
  private sheetWidth: number;
  public propertyForm: UntypedFormGroup = this.fb.group({
    searchStr: [''],
    state: ['']
  });
  public lastModified: Date;
  public tabStripVisible = false;
  public spreadBackColor = 'white';
  public hostStyle = {
    height: '100%',
    width: '100%'
  };
  public data: unknown;
  private unsubscribe = new Subject<void>();
  public propertyDescColumn = 0;
  public propertyNoColumn = 1;
  public propertyNameColumn = 2;
  public stateColumn = 3;
  public countyColumn = 4;
  public operatorColumn = 5;
  public purchaserColumn = 6;
  public blockChainColumn = 7;
  public blockChainTransIdColumn = 8;
  public gridColCount = 9;
  public allProperties: Property[] = [];
  public properties: Property[];
  public filteredProperties: Property[];

  // Sorting globals
  public orderBy: string = 'NAME';
  public sortAsc: boolean = true;

  public propertySearch: PropertySearch = {
    propertyType: 0,
    searchStr: [],
    state: null
  };

  constructor(
    @Inject(UntypedFormBuilder)
    private fb: UntypedFormBuilder,
    private propertyService: PropertyService,
    private datePipe: DatePipe,
    private location: Location,
    private router: Router,
    private storageService: StorageService,
  ) { }

  ngOnInit() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('list')) {
      this.properties = this.storageService.getCurrentAccountProperties() as unknown as Property[];
      this.allProperties = this.properties;
    } else {
      if (this.propertyService.getProperties().length != 0) {
        this.getProperties();
      } else {
        this.router.navigate(['/dashboard']);
      }
    }

    this.propertySearch;
    this.propertySearch.propertyType = 0;
    this.propertySearch.searchStr = [];
    this.propertyForm.get('state').setValue("-1");
  }

  get states() {
    return this.allProperties.reduce((uniqueStates, property) => {
      const state = property.geographicalData.county.state;
      if (state && !uniqueStates.find(s => s.id === state.id)) {
        uniqueStates.push({ name: state.name, id: state.id, abbr: state.abbr });
      }
      return uniqueStates;
    }, []);
  }
  trackByStateId(index: number, item: any) {
    return item.id;
  }

  public navigateToPropertyDetail(propertyId: number) {
    this.storageService.setCurrentPropertyId(propertyId);
    this.router.navigate(['/propertyDetail']);
  }


  public exportToCSV() {
    const csvData = this.convertToCSV(this.properties);
    const blob = new Blob([csvData], { type: 'text/csv' });
    saveAs(blob, 'properties.csv');
  }

  private convertToCSV(data: Property[]): string {
    const header = ['Description', 'Property', 'Name', 'State', 'County', 'Operator'].join(',');
    const rows = data.map(property => {

      return [
        property.description || '',
        property.propertyNo || '',
        property.name || '',
        property.geographicalData?.county?.state?.name || '',
        property.geographicalData?.county?.name || ''
      ].join(',');
    });
    return `${header}\n${rows.join('\n')}`;
  }



  getProperties(): void {
    if (!this.properties) {
      this.properties = this.propertyService.getProperties();
      this.allProperties = this.properties;
    }
  }

  transformDate(date) {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }


  setPropertyValues(filteredProperties: Property[]): Property[] {
    if (Array.isArray(filteredProperties) && filteredProperties.length > 0) {
      filteredProperties.forEach(property => {
        property.geographicalData.county.state.name = property.geographicalData.county ? property.geographicalData.county.state ? property.geographicalData.county.state.name : '' : '';
        property.geographicalData.county.name = property.geographicalData.county ? property.geographicalData.county.name : '';
      });
    }
    return filteredProperties;
  }

  getUniqueOperators(wells: any[]): string[] {
    const uniqueOperators = new Set();
    return wells
      .filter(well => {
        const operatorName = well?.operator?.name;
        if (operatorName && !uniqueOperators.has(operatorName)) {
          uniqueOperators.add(operatorName);
          return true;
        }
        return false;
      })
      .map(well => well.operator.name);
  }

  private filter() {
    this.properties = [];

    const tempProperties = [];

    const today = new Date();
    if (Array.isArray(this.allProperties) && this.allProperties.length > 0) {
      this.allProperties.forEach((propertyObject: Property) => {
        if (propertyObject != null && Object.keys(propertyObject).length !== 0) {
          let includeProperty = true;
          if (this.propertySearch.state !== '') {
            includeProperty = (propertyObject.geographicalData.county.state.id === Number(this.propertySearch.state));
          }
          if (includeProperty && this.propertySearch.searchStr.length > 0) {
            this.propertySearch.searchStr.forEach((searchSubStr) => {
              includeProperty = includeProperty
                && ((propertyObject.propertyNo.indexOf(searchSubStr) !== -1)
                  || (propertyObject.name.toUpperCase().indexOf(searchSubStr.toUpperCase()) !== -1)
                  || (propertyObject.description.toUpperCase().indexOf(searchSubStr.toUpperCase()) !== -1)
                  || (propertyObject.geographicalData.county.state.name.toUpperCase().indexOf(searchSubStr.toUpperCase()) !== -1)
                  || (propertyObject.geographicalData.county.name.toUpperCase().indexOf(searchSubStr.toUpperCase()) !== -1));
            });
          }

          if (this.propertySearch.propertyType > 0 && includeProperty) {
            if (this.propertySearch.propertyType === 1) {
              if (propertyObject.effectiveFrom !== null) {
                includeProperty = propertyObject.effectiveFrom >= today;
              } else {
                includeProperty = false;
              }
            } else if (this.propertySearch.propertyType === 3) {
              includeProperty = false;
            } else {
              if (propertyObject.effectiveFrom !== null) {
                if (propertyObject.effectiveFrom <= today) {
                    includeProperty = true;
                } else {
                  includeProperty = false;
                }
              } else {
                includeProperty = true;
              }
            }
          }

          if (includeProperty) {
            tempProperties.push(propertyObject);
          }
        }
      });
    }

    this.properties = [...tempProperties];

  }

  public doSearch() {
    const searchInput = this.propertyForm.get('searchStr').value.toLowerCase();
    console.log('Search Input:', searchInput);

    if (searchInput === '') {
      this.properties = this.allProperties;
      return;
    }

    this.properties = this.properties.filter(property =>
      property.propertyNo.toLowerCase().includes(searchInput) ||
      property.name.toLowerCase().includes(searchInput) ||
      property.description.toLowerCase().includes(searchInput) ||
      property.geographicalData.county.state.name.toLowerCase().includes(searchInput) ||
      property.geographicalData.county.name.toLowerCase().includes(searchInput)
    );

  }

  public onStateChange() {
    if (this.propertyForm.get('state').value === "-1") {
      this.propertySearch.state = '-1';
      this.properties = this.allProperties;
    } else {
      this.propertySearch.state = this.propertyForm.get('state').value;
      this.filter();
    }
  }

  public btnAllProperties() {
    this.propertySearch.propertyType = 0;
    this.filter();
  }

  public btnInProgressProperties() {
    this.propertySearch.propertyType = 1;
    this.filter();
  }

  public btnActiveProperties() {
    this.propertySearch.propertyType = 2;
    this.filter();
  }

  public btnInactiveProperties() {
    this.propertySearch.propertyType = 3;
    this.filter();
  }

  public onBack() {
    this.location.back();
  }

  public workbookInit(args) {
    this.spread = args.spread as GC.Spread.Sheets.Workbook;
    this.sheet = this.spread.getActiveSheet();

    this.sheetWidth = this.sheet.getViewportWidth(1);
    console.info('SheetWidth: ', this.sheetWidth);
  }

  /**
   * Sorts the properties array by the given orderBy field.
   * If newOrderBy is the same as current orderBy, toggles sortAsc.
   * Otherwise, sets orderBy to newOrderBy and resets sortAsc to true.
   * Then sorts the properties array accordingly.
   */
  public sort(newOrderBy: string): void {
    if (newOrderBy === this.orderBy) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.orderBy = newOrderBy;
      this.sortAsc = true;
    }

    if (!this.properties) return;
    
    this.properties.sort((a: any, b: any) => {
      let aValue = a[this.orderBy];
      let bValue = b[this.orderBy];
      // Handle nested fields if needed (e.g., state, county)
      if (this.orderBy === 'NAME') {
        aValue = a.name || '';
        bValue = b.name || '';
      } else if (this.orderBy === 'DESCRIPTION') {
        aValue = a.description || '';
        bValue = b.description || '';
      } else if (this.orderBy === 'PRODUCING') {
        aValue = a.producing || '';
        bValue = b.producing || '';      
      } else if (this.orderBy === 'STATE') {
        aValue = a.geographicalData?.county?.state?.name || '';
        bValue = b.geographicalData?.county?.state?.name || '';
      } else if (this.orderBy === 'COUNTY') {
        aValue = a.geographicalData?.county?.name || '';
        bValue = b.geographicalData?.county?.name || '';
      } else if (this.orderBy === 'OPERATOR') {
        aValue = a.wells && a.wells.length > 0 ? a.wells[0].operator.name : '';
        bValue = b.wells && b.wells.length > 0 ? b.wells[0].operator.name : '';
      }
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toUpperCase();
        bValue = bValue.toUpperCase();
      }
      if (aValue < bValue) return this.sortAsc ? -1 : 1;
      if (aValue > bValue) return this.sortAsc ? 1 : -1;
      return 0;
    });
  }

}
